import React, { FC, useContext, useState } from 'react';

import { Toggle, NumberInput, Separator, H3 } from '@smartshell/ui-kit';
import cn from 'classnames';

import { SettingsLock } from 'components';
import { useIsFeatureAvailable } from 'features';
import { LocalizationContext } from 'localization';
import { ESettingsAliases, SettingsContext } from 'settings';

import s from './OnlineBooking.module.scss';

export const OnlineBooking: FC = () => {
  const {
    state: {
      settings: {
        onlineBooking,
        onlineBookingDurationMin,
        onlineBookingDurationMax,
        // onlineBookingPenalty,
        bookingBlockDuration,
        onlineBookingDiscount,
      },
    },
    actions: { setSetting },
  } = useContext(SettingsContext);
  const { t } = useContext(LocalizationContext);

  const isAvailable = useIsFeatureAvailable('booking');

  const [minDuration, setMinDuration] = useState<number | ''>(
    onlineBookingDurationMin
  );
  const [maxDuration, setMaxDuration] = useState<number | ''>(
    onlineBookingDurationMax
  );
  const [bookingTime, setBookingTime] = useState<number | ''>(
    bookingBlockDuration
  );
  // const [penalty, setPenalty] = useState<number | ''>(onlineBookingPenalty);
  const [discount, setDiscount] = useState<number | ''>(onlineBookingDiscount);
  const [errors, setErrors] = useState<Record<string, string | undefined>>({});

  return (
    <>
      <div className={s.settings}>
        <div className={s.field}>
          <Toggle
            label={t('setting.onlineBooking')}
            description={t('settings.smartGamer.booking.description')}
            value={onlineBooking}
            onChange={(value: boolean) =>
              setSetting({
                alias: ESettingsAliases.ONLINE_BOOKING,
                value: value ? '1' : '0',
              })
            }
            variant="secondary"
            emptyBg
            rightContent={!isAvailable ? <SettingsLock /> : undefined}
            disabled={!isAvailable}
            className={s.toggle}
          />

          <Separator />

          <div className={s.inputContainers}>
            <div className={s.inputContainer}>
              <div className={cn(s.left_block, s.big_block)}>
                <H3>{t('settings.smartGamer.booking.discount.title')}</H3>
                <span className={s.subtitle}>
                  {t('settings.smartGamer.booking.discount.description')}
                </span>
              </div>
              <NumberInput
                value={discount}
                variant="secondary"
                onChange={(value) => {
                  if (value === '' || value < 0) {
                    setErrors((prev) => ({
                      ...prev,
                      discount: t('errors.min', '0', '%'),
                    }));
                  } else if (value > 100) {
                    setErrors((prev) => ({
                      ...prev,
                      discount: t('errors.max', 100, '%'),
                    }));
                  } else {
                    setErrors((prev) => ({
                      ...prev,
                      discount: undefined,
                    }));
                  }

                  setDiscount(value);
                }}
                onBlur={() => {
                  if (!errors.discount && discount !== onlineBookingDiscount)
                    return setSetting({
                      alias: ESettingsAliases.ONLINE_BOOKING_DISCOUNT,
                      value: discount.toString(),
                    });
                }}
                error={errors.discount}
                errorWrapperClassName={s.input}
                rightContent={'%'}
                bordered
                disabled={!onlineBooking || !isAvailable}
              />
            </div>

            <div className={s.inputContainer}>
              <div className={s.left_block}>
                <H3>{t('setting.onlineBooking.duration.min')}</H3>
                <span className={s.subtitle}>
                  {t('settings.smartGamer.booking.min_time.description')}
                </span>
              </div>
              <NumberInput
                value={minDuration}
                variant="secondary"
                onChange={(value) => {
                  if (value === '' || value < 1) {
                    setErrors((prev) => ({
                      ...prev,
                      minDuration: t('errors.min', 1, t('hour')),
                    }));
                  } else if (value > 24) {
                    setErrors((prev) => ({
                      ...prev,
                      minDuration: t('errors.max', 24, t('hour')),
                    }));
                  } else if (value > maxDuration) {
                    return setErrors((prev) => ({
                      ...prev,
                      minDuration: t(
                        'setting.onlineBooking.duration.errors.difference.min'
                      ),
                    }));
                  } else {
                    setErrors((prev) => ({
                      ...prev,
                      minDuration: undefined,
                    }));
                  }

                  setMinDuration(value);
                }}
                onBlur={() => {
                  if (
                    !errors.minDuration &&
                    minDuration !== '' &&
                    minDuration !== onlineBookingDurationMin
                  ) {
                    return setSetting({
                      alias: ESettingsAliases.ONLINE_BOOKING_MIN_DURATION,
                      value: (minDuration * 3600).toString(),
                    });
                  }
                }}
                errorWrapperClassName={s.input}
                error={errors.minDuration}
                rightContent={t('time.contracted.hours')}
                bordered
                disabled={!onlineBooking || !isAvailable}
              />
            </div>

            <div className={s.inputContainer}>
              <div className={s.left_block}>
                <H3>{t('setting.onlineBooking.duration.max')}</H3>
                <span className={s.subtitle}>
                  {t('settings.smartGamer.booking.max_time.description')}
                </span>
              </div>
              <NumberInput
                value={maxDuration}
                variant="secondary"
                onChange={(value) => {
                  if (value === '' || value < 1) {
                    setErrors((prev) => ({
                      ...prev,
                      maxDuration: t('errors.min', 1, t('hour')),
                    }));
                  } else if (value > 24) {
                    setErrors((prev) => ({
                      ...prev,
                      maxDuration: t('errors.max', 24, t('hour')),
                    }));
                  } else if (minDuration > value) {
                    return setErrors((prev) => ({
                      ...prev,
                      maxDuration: t(
                        'setting.onlineBooking.duration.errors.difference.max'
                      ),
                    }));
                  } else {
                    setErrors((prev) => ({
                      ...prev,
                      maxDuration: undefined,
                    }));
                  }

                  setMaxDuration(() => value);
                }}
                onBlur={() => {
                  if (
                    !errors.maxDuration &&
                    maxDuration !== '' &&
                    maxDuration !== onlineBookingDurationMax
                  ) {
                    return setSetting({
                      alias: ESettingsAliases.ONLINE_BOOKING_MAX_DURATION,
                      value: (maxDuration * 3600).toString(),
                    });
                  }
                }}
                errorWrapperClassName={s.input}
                error={errors.maxDuration}
                rightContent={t('time.contracted.hours')}
                bordered
                disabled={!onlineBooking || !isAvailable}
              />
            </div>

            <div className={s.inputContainer}>
              <div className={s.left_block}>
                <H3>{t('settings.smartGamer.booking.time.title')}</H3>
                <span className={s.subtitle}>
                  {t('settings.smartGamer.booking.time.description')}
                </span>
              </div>
              <NumberInput
                value={bookingBlockDuration}
                variant="secondary"
                onChange={(value) => {
                  if (value === '' || value < 0) {
                    setErrors((prev) => ({
                      ...prev,
                      bookingTime: t('errors.min', '0', t('minute')),
                    }));
                  } else if (value > 60) {
                    setErrors((prev) => ({
                      ...prev,
                      bookingTime: t('errors.max', 60, t('minute')),
                    }));
                  } else {
                    setErrors((prev) => ({
                      ...prev,
                      bookingTime: undefined,
                    }));
                  }

                  setBookingTime(value);
                }}
                onBlur={() => {
                  if (!errors.bookingTime && bookingTime !== '') {
                    return setSetting({
                      alias: ESettingsAliases.ONLINE_BOOKING_BLOCK_DURATION,
                      value: (bookingTime * 60).toString(),
                    });
                  }
                }}
                errorWrapperClassName={s.input}
                error={errors.bookingTime}
                rightContent={t('time.contracted.minutes')}
                bordered
                disabled={!onlineBooking || !isAvailable}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
